import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface Exam {
  id: number;
  title: string;
  description: string;
  board_id: number;
  board_name?: string;
  standard_id: number;
  standard_name?: string;
  subject_id: number;
  subject_name?: string;
  chapter_ids?: number[];
  duration_minutes: number;
  total_marks: number;
  passing_marks: number;
  difficulty_distribution?: {
    easy: number;
    medium: number;
    hard: number;
  };
  instructions: string;
  is_active: boolean;
  created_at: string;
}

const Exams: React.FC = () => {
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalExams, setTotalExams] = useState(0);

  // Master Data
  const [boards, setBoards] = useState<any[]>([]);
  const [standards, setStandards] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentExam, setCurrentExam] = useState<Partial<Exam>>({});

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState<Exam | null>(null);

  // Form selections (for cascading dropdowns inside dialog)
  const [dialogBoards, setDialogBoards] = useState<any[]>([]);
  const [dialogStandards, setDialogStandards] = useState<any[]>([]);
  const [dialogSubjects, setDialogSubjects] = useState<any[]>([]);
  const [dialogChapters, setDialogChapters] = useState<any[]>([]);

  useEffect(() => {
    loadExams();
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const loadMasterData = async () => {
    try {
      const res = await api.master.getBoards();
      setBoards(res.data.boards || []);
      setDialogBoards(res.data.boards || []);
    } catch (err) {
      console.error('Failed to load boards:', err);
    }
  };

  const loadExams = async () => {
    setLoading(true);
    try {
      const res = await api.exams.getAll({
        page: page + 1,
        limit: rowsPerPage,
      });
      setExams(res.data.exams || []);
      setTotalExams(res.data.total || res.data.pagination?.total || 0);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setIsEditing(false);
    setCurrentExam({
      title: '',
      description: '',
      instructions: '',
      duration_minutes: 60,
      total_marks: 100,
      passing_marks: 40,
      is_active: true,
      difficulty_distribution: { easy: 30, medium: 50, hard: 20 },
      chapter_ids: [],
    });
    setDialogStandards([]);
    setDialogSubjects([]);
    setDialogChapters([]);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = async (exam: Exam) => {
    setIsEditing(true);
    
    // Attempt to load full exam details in case we need to fetch specific chapter setups
    try {
      const res = await api.exams.getById(exam.id);
      const fullExam = res.data.exam || res.data;
      setCurrentExam(fullExam);
      
      // Load cascading dropdowns for the current exam's associations
      if (fullExam.board_id) {
        const stdRes = await api.master.getStandards(fullExam.board_id);
        setDialogStandards(stdRes.data.standards || []);
      }
      if (fullExam.standard_id) {
        const subRes = await api.master.getSubjects(fullExam.board_id, fullExam.standard_id);
        setDialogSubjects(subRes.data.subjects || []);
      }
      if (fullExam.subject_id) {
        const chapRes = await api.master.getChapters(fullExam.subject_id);
        setDialogChapters(chapRes.data.chapters || []);
      }

    } catch (err) {
      setCurrentExam(exam); // fallback to list view data
    }

    setOpenDialog(true);
  };

  const handleDialogBoardChange = async (boardId: number) => {
    setCurrentExam(prev => ({ ...prev, board_id: boardId, standard_id: undefined, subject_id: undefined, chapter_ids: [] }));
    try {
      const res = await api.master.getStandards(boardId);
      setDialogStandards(res.data.standards || []);
      setDialogSubjects([]);
      setDialogChapters([]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDialogStandardChange = async (standardId: number) => {
    setCurrentExam(prev => ({ ...prev, standard_id: standardId, subject_id: undefined, chapter_ids: [] }));
    try {
      if (currentExam.board_id) {
        const res = await api.master.getSubjects(currentExam.board_id, standardId);
        setDialogSubjects(res.data.subjects || []);
        setDialogChapters([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDialogSubjectChange = async (subjectId: number) => {
    setCurrentExam(prev => ({ ...prev, subject_id: subjectId, chapter_ids: [] }));
    try {
      const res = await api.master.getChapters(subjectId);
      setDialogChapters(res.data.chapters || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveExam = async () => {
    try {
      if (isEditing && currentExam.id) {
        await api.exams.update(currentExam.id, currentExam);
      } else {
        await api.exams.create(currentExam);
      }
      setOpenDialog(false);
      loadExams();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save exam');
    }
  };

  const handleDeleteClick = (exam: Exam) => {
    setExamToDelete(exam);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!examToDelete) return;
    try {
      await api.exams.delete(examToDelete.id);
      setDeleteDialogOpen(false);
      loadExams();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete exam');
    }
  };

  const handleToggleActive = async (exam: Exam) => {
    try {
      await api.exams.toggleActive(exam.id);
      loadExams();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle status');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Exams
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your test papers, configurations, and availability
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Exam
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table Section */}
      <TableContainer component={Paper} elevation={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Board / Std</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">Marks (Pass)</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No exams found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                exams.map((exam) => (
                  <TableRow key={exam.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {exam.title}
                      </Typography>
                      {exam.description && (
                         <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 200 }}>
                           {exam.description}
                         </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{exam.board_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{exam.standard_name}</Typography>
                    </TableCell>
                    <TableCell>{exam.subject_name}</TableCell>
                    <TableCell align="center">{exam.duration_minutes}m</TableCell>
                    <TableCell align="center">{exam.total_marks} ({exam.passing_marks})</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={exam.is_active ? 'Active' : 'Inactive'}
                        color={exam.is_active ? 'success' : 'default'}
                        size="small"
                        onClick={() => handleToggleActive(exam)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(exam)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(exam)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalExams}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Form Dialog for Create/Edit */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Exam' : 'Create New Exam'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Exam Title"
                fullWidth
                required
                value={currentExam.title || ''}
                onChange={(e) => setCurrentExam({ ...currentExam, title: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={currentExam.description || ''}
                onChange={(e) => setCurrentExam({ ...currentExam, description: e.target.value })}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth required>
                <InputLabel>Board</InputLabel>
                <Select
                  value={currentExam.board_id || ''}
                  label="Board"
                  onChange={(e) => handleDialogBoardChange(Number(e.target.value))}
                >
                  {dialogBoards.map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth required disabled={!currentExam.board_id}>
                <InputLabel>Standard</InputLabel>
                <Select
                  value={currentExam.standard_id || ''}
                  label="Standard"
                  onChange={(e) => handleDialogStandardChange(Number(e.target.value))}
                >
                  {dialogStandards.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth required disabled={!currentExam.standard_id}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={currentExam.subject_id || ''}
                  label="Subject"
                  onChange={(e) => handleDialogSubjectChange(Number(e.target.value))}
                >
                  {dialogSubjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {dialogChapters.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Chapters</InputLabel>
                  <Select
                    multiple
                    value={currentExam.chapter_ids || []}
                    onChange={(e) => setCurrentExam({ ...currentExam, chapter_ids: e.target.value as number[] })}
                    input={<OutlinedInput label="Chapters" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((value) => {
                          const chap = dialogChapters.find(c => c.id === value);
                          return <Chip key={value} label={chap ? chap.name : value} size="small" />;
                        })}
                      </Box>
                    )}
                  >
                    {dialogChapters.map((chapter) => (
                      <MenuItem key={chapter.id} value={chapter.id}>
                        <Checkbox checked={(currentExam.chapter_ids || []).indexOf(chapter.id) > -1} />
                        <ListItemText primary={chapter.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Duration (Minutes)"
                type="number"
                fullWidth
                required
                value={currentExam.duration_minutes || 60}
                onChange={(e) => setCurrentExam({ ...currentExam, duration_minutes: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Total Marks"
                type="number"
                fullWidth
                required
                value={currentExam.total_marks || 100}
                onChange={(e) => setCurrentExam({ ...currentExam, total_marks: Number(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Passing Marks"
                type="number"
                fullWidth
                required
                value={currentExam.passing_marks || 40}
                onChange={(e) => setCurrentExam({ ...currentExam, passing_marks: Number(e.target.value) })}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
               <Typography variant="subtitle2" gutterBottom>Difficulty Distribution (%)</Typography>
               <Grid container spacing={2}>
                 <Grid size={{ xs: 4 }}>
                    <TextField 
                      label="Easy" 
                      type="number" 
                      fullWidth 
                      value={currentExam.difficulty_distribution?.easy || ''}
                      onChange={(e) => setCurrentExam({
                        ...currentExam, 
                        difficulty_distribution: { ...currentExam.difficulty_distribution, easy: Number(e.target.value) } as any
                      })}
                    />
                 </Grid>
                 <Grid size={{ xs: 4 }}>
                    <TextField 
                      label="Medium" 
                      type="number" 
                      fullWidth 
                      value={currentExam.difficulty_distribution?.medium || ''}
                      onChange={(e) => setCurrentExam({
                        ...currentExam, 
                        difficulty_distribution: { ...currentExam.difficulty_distribution, medium: Number(e.target.value) } as any
                      })}
                    />
                 </Grid>
                 <Grid size={{ xs: 4 }}>
                    <TextField 
                      label="Hard" 
                      type="number" 
                      fullWidth 
                      value={currentExam.difficulty_distribution?.hard || ''}
                      onChange={(e) => setCurrentExam({
                        ...currentExam, 
                        difficulty_distribution: { ...currentExam.difficulty_distribution, hard: Number(e.target.value) } as any
                      })}
                    />
                 </Grid>
               </Grid>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                label="General Instructions"
                fullWidth
                multiline
                rows={3}
                value={currentExam.instructions || ''}
                onChange={(e) => setCurrentExam({ ...currentExam, instructions: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentExam.is_active ?? true}
                    onChange={(e) => setCurrentExam({ ...currentExam, is_active: e.target.checked })}
                    color="primary"
                  />
                }
                label="Is Active (Visible to Students)"
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveExam} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Exam</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the exam "{examToDelete?.title}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Exams;
